// src/pages/Telemedicine.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import nooLogo from '../assets/noo.png'; // Logo icon
import leftArrowIcon from '../assets/left.png'; // Back arrow icon

const statusStyles = {
  Scheduled: 'bg-[var(--teal)]/10 text-[var(--teal-deep)]',
  Completed: 'bg-[var(--mist)] text-[var(--ink)]/70',
  Cancelled: 'bg-[var(--coral)]/10 text-[var(--coral)]',
};

const Telemedicine = () => {
  // Mock data for teleconsultation appointments
  const [teleAppointments, setTeleAppointments] = useState(() => {
    const savedTeleAppointments = localStorage.getItem('clinicTeleAppointments');
    return savedTeleAppointments ? JSON.parse(savedTeleAppointments) : [
      {
        id: 'tele1',
        patientName: 'Nour Ali',
        doctorName: 'Dr. Ahmed Hassan',
        date: '2024-08-05',
        time: '10:00 AM',
        status: 'Scheduled',
        meetingLink: 'https://meet.google.com/abc-defg-hij' // Mock meeting link
      },
      {
        id: 'tele2',
        patientName: 'Fatma Khaled',
        doctorName: 'Dr. Sara Ali',
        date: '2024-08-05',
        time: '11:00 AM',
        status: 'Scheduled',
        meetingLink: 'https://meet.google.com/klm-nopq-rst'
      },
      {
        id: 'tele3',
        patientName: 'Mohamed Tarek',
        doctorName: 'Dr. Omar Mahmoud',
        date: '2024-08-06',
        time: '09:00 AM',
        status: 'Completed',
        meetingLink: 'https://meet.google.com/uvw-xyz-123'
      },
      {
        id: 'tele4',
        patientName: 'Layla Said',
        doctorName: 'Dr. Ahmed Hassan',
        date: '2024-08-06',
        time: '01:00 PM',
        status: 'Cancelled',
        meetingLink: 'https://meet.google.com/456-789-012'
      },
    ];
  });

  // Save tele-appointments to local storage whenever they change
  useEffect(() => {
    localStorage.setItem('clinicTeleAppointments', JSON.stringify(teleAppointments));
  }, [teleAppointments]);

  // State to simulate joining a call (for demonstration purposes)
  const [activeCall, setActiveCall] = useState(null); // Stores the ID of the appointment currently in a call

  // Function to simulate joining a call
  const handleJoinCall = (appointmentId, meetingLink) => {
    setActiveCall(appointmentId);
    // In a real application, you would redirect to the meeting link
    // or open it in a new tab/window.
    window.open(meetingLink, '_blank');
    // For demonstration, we'll set a timeout to simulate call end
    setTimeout(() => {
      setActiveCall(null);
      // Optionally update appointment status to 'Completed'
      setTeleAppointments(prevApps => prevApps.map(app =>
        app.id === appointmentId ? { ...app, status: 'Completed' } : app
      ));
    }, 5000); // Simulate a 5-second call
  };

  // Function to simulate ending a call (if needed, or automatically after timeout)
  const handleEndCall = () => {
    setActiveCall(null);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] p-4 md:p-8" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Header and Back Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Link to="/home">
            <img src={nooLogo} alt="Drogastro" className="w-15 h-12" />
          </Link>
          <Link
            to="/home"
            className="flex items-center gap-2 bg-white border border-[var(--mist)] hover:bg-[var(--mist)]/50
                       text-[var(--ink)] font-medium text-sm py-2 px-4 rounded-full shadow-sm transition-colors duration-200"
          >
            <img src={leftArrowIcon} alt="" className="w-4 h-4" /> Back to Home
          </Link>
        </div>
        <div>
          <span
            className="block text-[var(--coral)] text-xs tracking-[0.2em] uppercase mb-1"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            Live Consultations
          </span>
          <h1
            className="text-2xl md:text-3xl font-medium text-[var(--ink)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Telemedicine
          </h1>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white border border-[var(--mist)] shadow-sm rounded-2xl p-6 md:p-8">
        <h2 className="text-lg font-semibold text-[var(--ink)] mb-4">Upcoming &amp; Past Consultations</h2>
        {teleAppointments.length === 0 ? (
          <p className="text-[var(--ink)]/60 text-lg">No teleconsultations scheduled.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[var(--mist)]">
            <table className="min-w-full bg-white">
              <thead className="bg-[var(--mist)]/60">
                <tr>
                  {['Date', 'Time', 'Patient', 'Doctor', 'Status', 'Actions'].map((col) => (
                    <th
                      key={col}
                      className="py-3 px-4 text-left text-xs font-medium text-[var(--ink)]/60 uppercase tracking-wider border-b border-[var(--mist)]"
                      style={{ fontFamily: 'var(--font-mono)' }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teleAppointments.map(app => (
                  <tr key={app.id} className="border-b border-[var(--mist)] last:border-b-0 hover:bg-[var(--mist)]/20 transition-colors">
                    <td className="py-3 px-4 whitespace-nowrap text-[var(--ink)]">{app.date}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-[var(--ink)]">{app.time}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-[var(--ink)]">{app.patientName}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-[var(--ink)]/80">{app.doctorName}</td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles[app.status] || 'bg-[var(--mist)] text-[var(--ink)]/70'}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      {app.status === 'Scheduled' && (
                        <button
                          onClick={() => handleJoinCall(app.id, app.meetingLink)}
                          className={`bg-[var(--teal)] hover:bg-[var(--teal-deep)] text-white font-medium text-sm py-1.5 px-4 rounded-full transition-colors duration-200 ${activeCall === app.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={activeCall === app.id}
                        >
                          {activeCall === app.id ? 'In Call…' : 'Join Call'}
                        </button>
                      )}
                      {app.status === 'Completed' && (
                        <span className="text-[var(--ink)]/40 font-medium text-sm">Completed</span>
                      )}
                      {app.status === 'Cancelled' && (
                        <span className="text-[var(--coral)] font-medium text-sm">Cancelled</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Simple Call Simulation Display (Optional) */}
      {activeCall && (
        <div className="fixed bottom-4 right-4 bg-[var(--ink)] text-white p-4 rounded-2xl shadow-xl z-50 flex items-center gap-4">
          <span className="w-2 h-2 rounded-full bg-[var(--coral)] animate-pulse" aria-hidden="true" />
          <p className="text-sm">In call — Appointment {activeCall}</p>
          <button
            onClick={handleEndCall}
            className="bg-[var(--coral)] hover:brightness-95 text-white font-medium text-sm py-1.5 px-4 rounded-full transition-colors duration-200"
          >
            End Call
          </button>
        </div>
      )}
    </div>
  );
};

export default Telemedicine;
